import { Link } from '@tanstack/react-router';
import { ChevronRight, Compass } from 'lucide-react';
import { showsCalendar } from '../components/home/calendar-access.ts';
import { CalendarCard } from '../components/home/calendar-card.tsx';
import { PendingMessagesCard } from '../components/home/pending-messages-card.tsx';
import { TasksCard } from '../components/home/tasks-card.tsx';
import { EmptyState } from '../components/ui/empty-state.tsx';
import { groupNav, NAV, NAV_GROUP_LABEL, visibleNav } from '../lib/nav.ts';
import { useSession } from '../lib/session.tsx';
import { cn } from '../lib/utils.ts';

/**
 * Home, laid out as Leve's (TS-1681; Hriq `apps/app/.../home/home-dashboard.tsx` and
 * `home-tiles-grid.tsx`): a welcome header (eyebrow, "Welcome, <first name>!", role and
 * organisation; the date and the company tagline on the right) and the tools as tiles: a
 * rounded card, the icon in a violet circle, the title, the description with an arrow, a lift
 * on hover. The tools are the sidebar's, by section. API status lives in Settings.
 *
 * Between the two, the signed-in user's own day — never anyone else's: Tasks overdue or due
 * today, Pending messages (`messages.enabled`) and the Calendar of Calendly meetings
 * (`home.agenda`), in that order. "Today" is the viewer's day in their browser's time zone.
 */
const TODAY = new Intl.DateTimeFormat('en-US', {
  weekday: 'long',
  month: 'long',
  day: 'numeric',
  year: 'numeric',
});

export function HomePage() {
  const session = useSession();
  const showPending =
    session.isEnabled('messages.enabled') &&
    session.isEnabled('customers.enabled') &&
    session.can('customer.read');
  const showCalendar = showsCalendar(session);
  const { user, organisation } = session.me;
  const firstName = user.name.split(' ')[0] || user.name;
  const sections = groupNav(
    visibleNav(NAV, session).filter((n) => n.to !== '/' && n.to !== '/settings'),
  );

  return (
    <div className="pt-4">
      <header className="relative flex flex-col gap-4 overflow-hidden lg:flex-row lg:items-start lg:justify-between">
        <div className="space-y-2">
          <p className="text-muted-foreground text-xs font-medium tracking-[0.25em] uppercase">
            Good to see you,
          </p>
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Welcome, {firstName}!</h1>
          <p className="text-muted-foreground text-base">
            <span className="capitalize">{organisation.role}</span> · {organisation.name}
          </p>
        </div>
        <div className="relative flex flex-col gap-3 lg:items-end">
          <p className="text-muted-foreground text-sm">{TODAY.format(new Date())}</p>
          <svg
            aria-hidden="true"
            className="pointer-events-none absolute top-6 right-28 hidden h-24 w-80 text-violet-300/70 lg:block dark:text-violet-400/30"
            fill="none"
            viewBox="0 0 320 96"
          >
            {[0, 8, 16, 24, 32].map((offset) => (
              <path
                d={`M0 ${86 - offset / 2} C 70 ${80 - offset}, 110 ${10 + offset}, 180 ${14 + offset} S 280 ${70 - offset / 2}, 320 ${60 - offset / 2}`}
                key={offset}
                stroke="currentColor"
                strokeWidth="1"
              />
            ))}
          </svg>
          <p className="relative hidden text-right text-2xl leading-tight font-medium lg:block">
            People
            <br />
            Power
            <br />
            Progress.
          </p>
        </div>
      </header>

      <section className="mt-10" aria-labelledby="home-today">
        <h2
          id="home-today"
          className="text-muted-foreground mb-4 text-xs font-medium tracking-[0.25em] uppercase"
        >
          Today
        </h2>
        {/* Stacked below lg, each card as tall as its content. From lg, Team Tasks' shape: Tasks
            over Pending messages (60/40) beside the Calendar at full height; from 2xl, three
            columns of one height (the Calendar's). Each card scrolls inside. */}
        <div
          className={cn(
            'grid grid-cols-1 gap-4',
            showCalendar
              ? 'lg:h-[40rem] lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] 2xl:h-[38rem]'
              : 'lg:h-[38rem] lg:grid-cols-2',
            showCalendar &&
              showPending &&
              'lg:grid-rows-[minmax(0,3fr)_minmax(0,2fr)] 2xl:grid-cols-3 2xl:grid-rows-[minmax(0,1fr)]',
          )}
        >
          <TasksCard fill />
          {showPending && <PendingMessagesCard fill />}
          {showCalendar && (
            <div
              className={cn(
                'min-h-0',
                showPending &&
                  'lg:col-start-2 lg:row-span-2 lg:row-start-1 2xl:col-start-3 2xl:row-span-1',
              )}
            >
              <CalendarCard fill />
            </div>
          )}
        </div>
      </section>

      {sections.length === 0 ? (
        <EmptyState
          className="mt-10"
          icon={Compass}
          title="Nothing here yet"
          description="Ask an admin for access to the tools you need."
        />
      ) : (
        sections.map((section) => (
          <section key={section.group} className="mt-10" aria-labelledby={`tools-${section.group}`}>
            <h2
              id={`tools-${section.group}`}
              className="text-muted-foreground mb-4 text-xs font-medium tracking-[0.25em] uppercase"
            >
              {NAV_GROUP_LABEL[section.group]}
            </h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
              {section.items.map((t) => {
                const Icon = t.icon;
                return (
                  <Link
                    key={t.to}
                    to={t.to}
                    className="group bg-card relative flex min-h-[176px] min-w-0 flex-col rounded-2xl border p-5 shadow-xs transition-[box-shadow,border-color,transform] hover:-translate-y-0.5 hover:border-violet-200 hover:shadow-md focus-visible:ring-2 focus-visible:ring-violet-400 focus-visible:outline-none motion-reduce:hover:translate-y-0 dark:hover:border-violet-500/40"
                  >
                    <span className="flex size-14 items-center justify-center rounded-full bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300">
                      <Icon className="size-6" strokeWidth={1.75} aria-hidden />
                    </span>
                    <h3 className="text-foreground mt-4 text-base font-semibold">{t.label}</h3>
                    <span className="mt-1 flex items-start justify-between gap-3">
                      <span className="text-muted-foreground text-sm leading-snug">
                        {t.description}
                      </span>
                      <ChevronRight
                        className="text-muted-foreground mt-0.5 size-4 shrink-0 transition-transform group-hover:translate-x-0.5"
                        aria-hidden
                      />
                    </span>
                  </Link>
                );
              })}
            </div>
          </section>
        ))
      )}
    </div>
  );
}
